import React, { useEffect, useState } from 'react';
import * as emotion from 'emotion';
import * as emotionCore from '@emotion/core';
import * as emotionTheming from 'emotion-theming';
import { onConsentChange } from '@guardian/consent-management-platform';
import { getZIndex } from '@root/src/web/lib/getZIndex';
import { submitComponentEvent } from '@root/src/web/browser/ophan/ophan';
export const brazeVendorId = '5ed8c49c4b8ce4571c7ad801';
const containerStyles = emotion.css `
    position: fixed;
    bottom: -1px;
    width: 100%;
    ${getZIndex('banner')}
`;
export const hasRequiredConsents = () => new Promise((resolve) => {
    onConsentChange(({ tcfv2, ccpa }) => {
        const consentGivenUnderCcpa = ccpa && !ccpa.doNotSell;
        const consentGivenUnderTcfv2 = tcfv2 && tcfv2.vendorConsents[brazeVendorId];
        resolve(Boolean(consentGivenUnderCcpa || consentGivenUnderTcfv2));
    });
});
export const canShowPreChecks = ({ brazeSwitch, apiKey, isDigitalSubscriber, pageConfig, }) => Boolean(brazeSwitch &&
    apiKey &&
    isDigitalSubscriber &&
    !pageConfig.isPaidContent);
const getMessageFromBraze = async (apiKey, brazeUuid) => {
    const { default: appboy } = await import(
    /* webpackChunkName: "braze-web-sdk-core" */ '@braze/web-sdk-core');
    appboy.initialize(apiKey, {
        enableLogging: false,
        noCookies: true,
        baseUrl: 'https://sdk.fra-01.braze.eu/api/v3',
        sessionTimeoutInSeconds: 1,
        minimumIntervalBetweenTriggerActionsInSeconds: 0,
    });
    return new Promise((resolve) => {
        appboy.subscribeToInAppMessage((message) => {
            const { extras } = message;
            const logButtonClickWithBraze = (internalButtonId) => {
                const thisButton = new appboy.InAppMessageButton(`Button: ID ${internalButtonId}`, undefined, undefined, undefined, undefined, undefined, internalButtonId);
                appboy.logInAppMessageButtonClick(thisButton, message);
            };
            const logImpression = () => {
                // Log the impression with Braze
                appboy.logInAppMessageImpression(message);
            };
            if (extras) {
                const meta = {
                    dataFromBraze: extras,
                    logImpression,
                    logButtonClickWithBraze,
                };
                resolve({ result: true, meta });
            }
            else {
                resolve({ result: false });
            }
        });
        appboy.changeUser(brazeUuid);
        appboy.openSession();
    });
};
// We can show a Braze banner if:
// - The Braze switch is on
// - We have a Braze API key
// - The user is a digital subscriber
// - We're not on a Glabs paid content page
// - We've got a Braze UUID from the API, given a user's ID Creds
// - The user has given Consent via CCPA or TCFV2
// - The Braze websdk appboy initialisation does not throw an error
// - The Braze app Boy subscription to in app message returns meta info
export const canShow = async (asyncBrazeUuid, isDigitalSubscriber) => {
    const { brazeSwitch } = window.guardian.config.switches;
    const apiKey = window.guardian.config.page.brazeApiKey;
    if (!canShowPreChecks({
        brazeSwitch,
        apiKey,
        isDigitalSubscriber,
        pageConfig: window.guardian.config.page,
    })) {
        return { result: false };
    }
    const [brazeUuid, hasGivenConsent] = await Promise.all([
        asyncBrazeUuid,
        hasRequiredConsents(),
    ]);
    if (!(brazeUuid && hasGivenConsent)) {
        return { result: false };
    }
    try {
        return getMessageFromBraze(apiKey, brazeUuid);
    }
    catch (e) {
        return { result: false };
    }
};
const BrazeBannerWithSatisfiedDependencies = ({ BrazeComponent, meta, }) => {
    useEffect(() => {
        // Log the impression with Braze
        meta.logImpression();
        // Log VIEW event with Ophan
        submitComponentEvent({
            component: {
                componentType: 'RETENTION_ENGAGEMENT_BANNER',
                id: meta.dataFromBraze.componentName,
            },
            action: 'VIEW',
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (React.createElement("div", { className: containerStyles },
        React.createElement(BrazeComponent, { logButtonClickWithBraze: meta.logButtonClickWithBraze, submitComponentEvent: submitComponentEvent, componentName: meta.dataFromBraze.componentName, brazeMessageProps: meta.dataFromBraze })));
};
export const BrazeBanner = ({ meta }) => {
    const [BrazeComponent, setBrazeComponent] = useState();
    useEffect(() => {
        if (meta) {
            // TODO: unify the way we handle sharing these deps (this is
            // duplicated in SlotBodyEnd). Probably via the automat client
            // library.
            window.guardian.automat = {
                react: React,
                preact: React,
                emotionCore,
                emotionTheming,
                emotion,
            };
            import(
            /* webpackChunkName: "guardian-braze-components" */ '@guardian/braze-components')
                .then((module) => {
                setBrazeComponent(() => module.BrazeMessage);
            })
                .catch((error) => window.guardian.modules.sentry.reportError(error, 'braze-banner'));
        }
    }, [meta]);
    if (BrazeComponent && meta) {
        return (React.createElement(BrazeBannerWithSatisfiedDependencies, { BrazeComponent: BrazeComponent, meta: meta }));
    }
    return React.createElement("div", null);
};
